import React, { Suspense, lazy, useEffect, useState } from "react";

const InStoreOrderDetailsModal = lazy(() =>
  import("../Components/OrdersNewUI/InstoreOrder/InStoreOrderDetailsModal"),
);

/** Matches /order/store-reporting/order-summary/<merchant_id>/<order_id> */
const ORDER_SUMMARY_HREF =
  /\/order\/store-reporting\/order-summary\/([^/?#]+)\/([^/?#]+)/;

/**
 * Mounted once at the app root: turns every "Order Id" / "View Details" link
 * that points at the order-summary page into the shared Order Details popup
 * (the same modal /merchants/order-new opens), without touching the ~20 report,
 * dashboard and customer screens that render those links.
 *
 * Capture-phase listener so it runs before react-router's Link handler. Plain,
 * modified (ctrl/cmd/shift/alt) and middle clicks are all intercepted, so the
 * order-summary page never opens in a new tab.
 */
export default function OrderSummaryPopupInterceptor() {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const onClick = (e) => {
      // auxclick also fires for right-click; only middle-click opens a tab.
      if (e.type === "auxclick" && e.button !== 1) return;
      const el = e.target;
      const anchor = el && el.closest ? el.closest("a[href]") : null;
      if (!anchor) return;
      const match = ORDER_SUMMARY_HREF.exec(anchor.getAttribute("href") || "");
      if (!match) return;

      e.preventDefault();
      e.stopPropagation();
      setTarget({
        merchantId: decodeURIComponent(match[1]),
        orderId: decodeURIComponent(match[2]),
      });
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, []);

  if (!target) return null;

  return (
    <Suspense fallback={null}>
      <InStoreOrderDetailsModal
        open
        onClose={() => setTarget(null)}
        order={{ order_id: target.orderId }}
        merchantId={target.merchantId}
      />
    </Suspense>
  );
}
